import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import MobileToolbar from 'components/Layout/MobileShell/MobileToolbar';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import { useGetEntidadesQuery } from 'services/api/windx';
import {
  Entidade,
  EntidadeSimples,
  EntidadesListParams,
  Usina,
  WindxTela,
} from 'helper/windx';
import {
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  selectWindxDataSimulacao,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import LinhaSelecao from '../LinhaSelecao';
import Rodape from '../Rodape';

interface EntidadesProps {
  open: boolean;
  dismiss: () => void;
  subdivisao: number | undefined;
  permiteSelecaoVazia: boolean;
}

// Subsistemas sao 5 itens (SIN + 4) — busca e filtros nao pagam a barra.
const SUBDIVISAO_SUBSISTEMAS = 1;

function rotulo(e: Entidade): string {
  const simples = e as EntidadeSimples;
  return simples.label || simples.nome || (e as Usina).nome || '';
}

/**
 * Seletores de entidade que vem da rota `entidades/list` no mobile:
 * subsistemas (subdivisao=1) e o caminho legado (demais subdivisoes).
 *
 * Junta os dois porque a fonte de dados e as regras sao as mesmas — o que muda
 * e o modo de selecao. Em subsistemas fora do Comparativo o desktop usa RADIO
 * (exatamente uma entidade); no Comparativo vira CHECKBOX e SIN e combinavel
 * com os individuais (bug PO 2026-05-19 — nao ha agregacao que cause dupla
 * contagem na tabela).
 *
 * Preservados do desktop: auto-remocao de entidades que ficaram indisponiveis,
 * "(sem dados)" com o item desabilitado, "nenhuma marcada inclui todas" no
 * Comparativo e o Aplicar bloqueado quando a selecao vazia nao e permitida.
 */
export default function Entidades({
  open, dismiss, subdivisao, permiteSelecaoVazia,
}: EntidadesProps) {
  const dispatch = useDispatch();
  const idsSelecionados = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);

  const [idsLocal, setIdsLocal] = useState<number[]>(idsSelecionados);
  const [busca, setBusca] = useState('');

  const ehSubsistemas = subdivisao === SUBDIVISAO_SUBSISTEMAS;
  const modo: 'radio' | 'checkbox' = ehSubsistemas && !permiteSelecaoVazia ? 'radio' : 'checkbox';

  const queryArg = useMemo<EntidadesListParams | undefined>(() => {
    if (subdivisao === undefined) return undefined;
    const arg: EntidadesListParams = { subdivisao };
    if (telaAtiva === 'sh' || telaAtiva === 'sm') {
      arg.tela = telaAtiva as WindxTela;
    }
    // WINDX-034: dataSimulacao eh fonte unica do modo de operacao.
    if (dataSimulacao) arg.data_simulacao = dataSimulacao;
    return arg;
  }, [subdivisao, telaAtiva, dataSimulacao]);

  const {
    data, isLoading, error,
  } = useGetEntidadesQuery(queryArg as EntidadesListParams, { skip: !queryArg || !open });

  const entidades = useMemo<Entidade[]>(() => data ?? [], [data]);

  const idsIndisponiveis = useMemo<Set<number>>(
    () => new Set(entidades.filter((e) => e.disponivel === false).map((e) => e.id)),
    [entidades],
  );

  // Auto-remove da selecao entidades que viraram indisponiveis (trocou de tela
  // ou de data_simulacao). Atualiza o buffer local e o Redux.
  useEffect(() => {
    if (idsIndisponiveis.size === 0) return;
    setIdsLocal((prev) => prev.filter((id) => !idsIndisponiveis.has(id)));
    const sobra = idsSelecionados.filter((id) => !idsIndisponiveis.has(id));
    if (sobra.length !== idsSelecionados.length) {
      dispatch(setIdsEntidades(sobra));
    }
  }, [idsIndisponiveis, idsSelecionados, dispatch]);

  // Sincroniza buffer local com Redux quando o sheet abre.
  useEffect(() => {
    if (open) {
      setIdsLocal(idsSelecionados);
      setBusca('');
    }
    // Intencional: `idsSelecionados` omitido para nao sobrescrever escolhas
    // locais se outro efeito despachar enquanto o sheet esta aberto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subdivisao]);

  const filtradas = useMemo(() => {
    if (!busca.trim()) return entidades;
    const q = busca.trim().toLowerCase();
    return entidades.filter((e) => rotulo(e).toLowerCase().includes(q));
  }, [entidades, busca]);

  const toggle = (id: number) => {
    if (modo === 'radio') {
      setIdsLocal([id]);
      return;
    }
    setIdsLocal((prev) => (
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    ));
  };

  const selecionarTodos = () => setIdsLocal(
    entidades.filter((e) => e.disponivel !== false).map((e) => e.id),
  );

  const handleAplicar = () => {
    // 2026-05-29: nao dispara realizarConsulta — ver SeletorSubsistemas.
    dispatch(setIdsEntidades(idsLocal));
    dismiss();
  };

  const totalDisponiveis = entidades.filter((e) => e.disponivel !== false).length;
  const semSelecaoEhTodas = idsLocal.length === 0 && permiteSelecaoVazia && telaAtiva === 'sm';
  const titulo = ehSubsistemas ? 'Selecionar subsistemas' : 'Selecionar entidades';
  const resumo = idsLocal.length === 0
    ? 'Nenhuma selecionada'
    : `${idsLocal.length} de ${totalDisponiveis} selecionada${idsLocal.length === 1 ? '' : 's'}`;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title={titulo}
      className={`windx-seletor-sheet${ehSubsistemas ? '' : ' windx-seletor-sheet--grande'}`}
    >
      <>
        {subdivisao === undefined && (
        <p className="windx-seletor-sheet-aviso">
          Escolha uma subdivisão espacial para listar as entidades.
        </p>
        )}

        {subdivisao !== undefined && error && (
        <Alert variant="danger" className="m-0">
          Não foi possível carregar as entidades. Tente novamente em instantes.
        </Alert>
        )}

        {subdivisao !== undefined && !error && (
        <>
          {!ehSubsistemas && (
            <MobileToolbar
              searchValue={busca}
              onSearchChange={(e) => setBusca(e.target.value)}
              setSearchQuery={setBusca}
              searchId="windx-busca-entidades-mobile"
              searchPlaceholder="Buscar entidade"
              filterActive={false}
              filterAriaLabel="Filtrar entidades"
              appliedFilters={[]}
              onFilterClick={() => {}}
              onClearAll={() => setBusca('')}
              resultsCount={filtradas.length}
              resultsNoun="entidade"
              loading={isLoading}
            />
          )}

          {semSelecaoEhTodas && totalDisponiveis > 0 && (
            <p className="windx-seletor-sheet-aviso">
              <strong>Nenhuma marcada inclui todas</strong>
              {' — a consulta vai retornar dados das '}
              {totalDisponiveis}
              {' entidade'}
              {totalDisponiveis === 1 ? '' : 's'}
              {' disponíve'}
              {totalDisponiveis === 1 ? 'l' : 'is'}
              .
            </p>
          )}

          <div className="windx-seletor-sheet-lista">
            <MobileListState
              loading={isLoading}
              isEmpty={filtradas.length === 0}
              emptyMessage="Nenhuma entidade encontrada."
            >
              {filtradas.map((e) => {
                const indisponivel = e.disponivel === false;
                return (
                  <LinhaSelecao
                    key={e.id}
                    inputId={`windx-entidade-mobile-${e.id}`}
                    label={rotulo(e)}
                    detalhe={indisponivel ? 'sem dados' : undefined}
                    selecionado={idsLocal.includes(e.id)}
                    onToggle={() => toggle(e.id)}
                    desabilitado={indisponivel}
                    modo={modo}
                    titulo={indisponivel
                      ? 'Sem dados disponíveis para a tela / data selecionada'
                      : undefined}
                  />
                );
              })}
            </MobileListState>
          </div>

          <Rodape
            selecionadas={idsLocal.length}
            resumo={resumo}
            onLimpar={() => setIdsLocal([])}
            onCancelar={dismiss}
            onAplicar={handleAplicar}
            aplicarDesabilitado={!permiteSelecaoVazia && idsLocal.length === 0}
            onSelecionarTodos={modo === 'checkbox' ? selecionarTodos : undefined}
          />
        </>
        )}
      </>
    </BottomSheet>
  );
}
